const normalizeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = normalizeValue(value[key]);
      return acc;
    }, {});
  }
  return String(value);
};

const formatPrimitive = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    return String(value);
  }
  const str = String(value);
  return str.trim().length ? str : '—';
};

const prettifyKey = (raw) => {
  if (!raw) return '—';
  if (/^\d+$/.test(raw)) return `Elemento ${raw}`;
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const summarizeObject = (value) => {
  if (!value || typeof value !== 'object') return formatPrimitive(value);
  const normalized = normalizeValue(value);
  return JSON.stringify(normalized, null, 2);
};

const hasTextContent = (value) => (typeof value === 'string' ? value.trim().length > 0 : false);

const hasFileMeta = (value) => {
  if (!value || typeof value !== 'object') return false;
  return Boolean(value.url || value.downloadURL || value.src || value.name);
};

const hasPathologyData = (entry) => {
  if (!entry || typeof entry !== 'object') return false;
  if (entry.presente) return true;
  if (hasTextContent(entry.diagnosi)) return true;
  if (hasTextContent(entry.note)) return true;
  if (hasTextContent(entry.cm)) return true;
  if (hasFileMeta(entry.certificazioneFile)) return true;
  return false;
};

const filterPathologyGroup = (group) => {
  if (!group || typeof group !== 'object') return null;
  const filtered = Object.keys(group).reduce((acc, key) => {
    if (hasPathologyData(group[key])) acc[key] = group[key];
    return acc;
  }, {});
  return Object.keys(filtered).length ? filtered : null;
};

const PATHOLOGY_FIELD_ORDER = ['presente', 'diagnosi', 'certificazioneFile', 'note'];
const MUSCLE_PATHOLOGY_FIELD_ORDER = ['presente', 'diagnosi', 'cm', 'certificazioneFile', 'note'];

const buildOrderMap = (items = []) =>
  items.reduce((acc, key, index) => {
    acc[key] = index;
    return acc;
  }, {});

const PATHOLOGY_FIELD_ORDER_MAP = buildOrderMap(PATHOLOGY_FIELD_ORDER);
const MUSCLE_PATHOLOGY_FIELD_ORDER_MAP = buildOrderMap(MUSCLE_PATHOLOGY_FIELD_ORDER);

const buildGroupOrderMap = (group) => {
  const order = {};
  if (!group || typeof group !== 'object') return order;
  Object.keys(group).forEach((key, index) => {
    order[key] = index;
  });
  return order;
};

const comparePathologyEntries = (entryA, entryB, groupOrderMap, fieldOrderMap) => {
  const groupA = entryA.path[1] || '';
  const groupB = entryB.path[1] || '';
  if (groupA !== groupB) {
    const orderA = groupOrderMap[groupA];
    const orderB = groupOrderMap[groupB];
    if (orderA !== undefined || orderB !== undefined) {
      return (orderA ?? Number.MAX_SAFE_INTEGER) - (orderB ?? Number.MAX_SAFE_INTEGER);
    }
    return groupA.localeCompare(groupB);
  }
  const fieldA = entryA.path[2] || '';
  const fieldB = entryB.path[2] || '';
  if (fieldA !== fieldB) {
    const indexA = fieldOrderMap[fieldA];
    const indexB = fieldOrderMap[fieldB];
    if (indexA !== undefined || indexB !== undefined) {
      return (indexA ?? Number.MAX_SAFE_INTEGER) - (indexB ?? Number.MAX_SAFE_INTEGER);
    }
    return fieldA.localeCompare(fieldB);
  }
  return 0;
};

const formatDisplayValue = (raw, kind) => {
  if (kind === 'array-primitive') {
    const list = Array.isArray(raw) ? raw : [];
    return list.length ? list.map(formatPrimitive).join(', ') : '—';
  }
  if (kind === 'array-object') {
    if (!Array.isArray(raw) || !raw.length) return '—';
    return raw
      .map((item, index) => {
        const title = `#${index + 1}`;
        if (item && typeof item === 'object') {
          const body = summarizeObject(item);
          return `${title}\n${body}`;
        }
        return `${title}: ${formatPrimitive(item)}`;
      })
      .join('\n\n');
  }
  if (kind === 'object') {
    return summarizeObject(raw);
  }
  if (Array.isArray(raw)) {
    return raw.length ? raw.map(formatPrimitive).join(', ') : '—';
  }
  return formatPrimitive(raw);
};

const flattenAnswers = (answers = {}) => {
  const entries = [];

  const pushEntry = (path, rawValue, kind) => {
    const key = path.join('.');
    const root = path[0] || '(root)';
    const segments = path.slice(1);
    const display = formatDisplayValue(rawValue, kind);
    entries.push({
      key,
      root,
      path,
      segments,
      label: path.map(prettifyKey).join(' › ') || prettifyKey(root),
      labelInGroup: segments.length ? segments.map(prettifyKey).join(' › ') : prettifyKey(root),
      display,
      raw: rawValue,
      kind,
      multiline: display.includes('\n'),
    });
  };

  const visit = (value, path) => {
    if (value === null || value === undefined) {
      pushEntry(path, value, 'empty');
      return;
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        pushEntry(path, value, 'array-empty');
        return;
      }
      const primitives = value.every(
        (item) => item === null || ['string', 'number', 'boolean'].includes(typeof item),
      );
      if (primitives) {
        pushEntry(path, value, 'array-primitive');
        return;
      }
      pushEntry(path, value, 'array-object');
      value.forEach((item, index) => visit(item, [...path, `${index + 1}`]));
      return;
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (!keys.length) {
        pushEntry(path, value, 'object-empty');
        return;
      }
      keys.forEach((key) => {
        visit(value[key], [...path, key]);
      });
      return;
    }

    if (typeof value === 'boolean') {
      pushEntry(path, value, 'boolean');
      return;
    }

    if (typeof value === 'number') {
      pushEntry(path, value, 'number');
      return;
    }

    pushEntry(path, value, 'string');
  };

  Object.keys(answers || {})
    .sort((a, b) => a.localeCompare(b))
    .forEach((key) => {
      visit(answers[key], [key]);
    });

  return entries;
};

const groupEntries = (entries, searchTerm, options = {}) => {
  const needle = (searchTerm || '').trim().toLowerCase();
  const providedAnswers =
    options && Object.prototype.hasOwnProperty.call(options, 'answers') ? options.answers : undefined;
  const sanitizedAnswers = sanitizeQuestionarioAnswers(providedAnswers);
  const pathologyGroupOrder = buildGroupOrderMap(sanitizedAnswers?.patologie);
  const musclePathologyGroupOrder = buildGroupOrderMap(sanitizedAnswers?.patologieMuscolo);
  const shouldSkipEntry = (entry) => {
    if (!sanitizedAnswers || typeof sanitizedAnswers !== 'object') return false;
    const groupKey = entry.path[1];
    if (entry.root === 'patologie') {
      return !sanitizedAnswers.patologie || !sanitizedAnswers.patologie[groupKey];
    }
    if (entry.root === 'patologieMuscolo') {
      return !sanitizedAnswers.patologieMuscolo || !sanitizedAnswers.patologieMuscolo[groupKey];
    }
    return false;
  };

  const grouping = new Map();

  entries.forEach((entry) => {
    const haystack = `${entry.label} ${entry.display}`.toLowerCase();
    if (needle && !haystack.includes(needle)) return;
    if (shouldSkipEntry(entry)) return;

    const root = entry.root;
    if (!grouping.has(root)) {
      grouping.set(root, {
        root,
        rootLabel: prettifyKey(root),
        items: [],
      });
    }
    grouping.get(root).items.push(entry);
  });

  return Array.from(grouping.values())
    .map((group) => ({
      ...group,
      items: group.items.sort((a, b) => {
        if (group.root === 'patologie') {
          const cmp = comparePathologyEntries(a, b, pathologyGroupOrder, PATHOLOGY_FIELD_ORDER_MAP);
          if (cmp !== 0) return cmp;
        } else if (group.root === 'patologieMuscolo') {
          const cmp = comparePathologyEntries(a, b, musclePathologyGroupOrder, MUSCLE_PATHOLOGY_FIELD_ORDER_MAP);
          if (cmp !== 0) return cmp;
        }
        return a.label.localeCompare(b.label);
      }),
    }))
    .sort((a, b) => a.rootLabel.localeCompare(b.rootLabel));
};

const isAffirmative = (value) => String(value ?? '').trim().toLowerCase() === 'si';

const createSectionDefs = (defs) =>
  defs.map((def) => ({
    ...def,
    fields: def.fields || [],
    fieldPrefixes: def.fieldPrefixes || [],
    orderMap: (def.fields || []).reduce((acc, field, index) => {
      acc[field] = index;
      return acc;
    }, {}),
  }));

const QUESTIONARIO_SECTION_DEFS = createSectionDefs([
  {
    key: 'informazioni_generali',
    title: 'Informazioni generali',
    fields: ['professione', 'turnista', 'oreLavorative', 'stileVita', 'oreSonno', 'qualitaSonno', 'periodoStressante'],
  },
  {
    key: 'anamnesi_sportiva',
    title: 'Anamnesi sportiva',
    fields: [
      'allenamentoAttuale',
      'tipiAllenamento',
      'esperienzaSala',
      'ptPresenza',
      'ptVideocall',
      'attrezzaturaCasa',
      'allenamentoLuogo',
      'allenamentoFrequenza',
      'allenamentoDurata',
    ],
  },
  {
    key: 'anamnesi_fisiologica',
    title: 'Anamnesi fisiologica',
    fields: ['alvo', 'cicloMestruale', 'gravidanze', 'litriAcqua', 'fumo', 'bevandeAlcoliche', 'haPatologieMetaboliche', 'haPatologieMuscolo'],
  },
  {
    key: 'patologie_metaboliche',
    title: 'Patologie metaboliche',
    fields: ['patologie', 'patologieAltre', 'dichiarazionePatologie'],
    visibleIf: (answers) => isAffirmative(answers?.haPatologieMetaboliche),
  },
  {
    key: 'patologie_muscolo',
    title: 'Patologie muscolo-scheletriche',
    fields: ['patologieMuscolo', 'patologieMuscoloAltre', 'infortuni', 'interventi', 'noteLimitazioni', 'farmaciAssunti'],
    visibleIf: (answers) => isAffirmative(answers?.haPatologieMuscolo),
  },
  {
    key: 'regime_alimentare',
    title: 'Regime alimentare',
    fields: ['regimeAlimentare', 'regimiControllatiPassato', 'regimeControllato', 'pastiGiorno', 'saltarePasti'],
  },
  {
    key: 'diario_alimentare',
    title: 'Diario alimentare',
    fields: ['diarioColazione', 'diarioMetaMattina', 'diarioPranzo', 'diarioMetaPomeriggio', 'diarioCena'],
  },
  {
    key: 'integratori',
    title: 'Integratori',
    fields: ['integratoriUsati', 'integratoriUsatiQuali', 'integratoriAttuali', 'integratoriAttualiQuali'],
  },
  {
    key: 'note_percorso',
    title: 'Obiettivi e note',
    fields: ['obiettivoPercorso', 'alimentiDaEscludere', 'responsabilitaAlimenti'],
  },
  {
    key: 'misurazioni',
    title: 'Misurazioni',
    fields: [
      'misurePreciclo',
      'peso',
      'altezza',
      'fianchi',
      'addome',
      'torace',
      'coscia',
      'braccio',
      'massaMagra',
      'massaGrassa',
      'acquaCorporea',
      'misurazioniNote',
    ],
  },
  { key: 'foto', title: 'Foto', fields: ['foto'], fieldPrefixes: ['foto'] },
  { key: 'idoneita', title: 'Idoneità', fields: ['idoneita'], fieldPrefixes: ['idoneita'] },
  { key: 'privacy', title: 'Privacy', fields: ['privacy'], fieldPrefixes: ['privacy'] },
  { key: 'termini', title: 'Termini e condizioni', fields: ['accettoTermini'] },
]);

const CHECK_SECTION_DEFS = createSectionDefs([
  {
    key: 'allenamento',
    title: 'Allenamento',
    fields: ['seduteSettimanali', 'attivitaAffiancate', 'noteAllenamento', 'adesioneAllenamento'],
  },
  {
    key: 'alimentazione',
    title: 'Alimentazione',
    fields: ['strategiaAlimentare', 'rispettoGrammature', 'pastiLiberi', 'acquaMedia', 'noteAlimentazione', 'adesioneAlimentazione'],
  },
  {
    key: 'recupero',
    title: 'Recupero',
    fields: ['recuperoOreSonno', 'recuperoSonnoContinuativo', 'recuperoSonnoMotivo', 'recuperoSonnoTrend', 'recuperoStress'],
  },
  { key: 'integrazione', title: 'Integrazione', fields: ['integrazioneAssunta', 'integrazioneDettagli'] },
  {
    key: 'misurazioni',
    title: 'Misurazioni',
    fields: [
      'misurePreciclo',
      'peso',
      'altezza',
      'fianchi',
      'addome',
      'torace',
      'coscia',
      'braccio',
      'massaMagra',
      'massaGrassa',
      'acquaCorporea',
      'misurazioniNote',
    ],
  },
  { key: 'foto', title: 'Foto', fields: ['foto'], fieldPrefixes: ['foto'] },
  { key: 'note_generali', title: 'Note generali', fields: ['noteGenerali'] },
]);

const matchesSection = (def, entry) => {
  const root = entry.path[0];
  if (def.fields.includes(root) || def.fields.includes(entry.key)) return true;
  return def.fieldPrefixes.some((prefix) => root === prefix || entry.key.startsWith(`${prefix}.`));
};

const buildSectionsFromDefs = (answers, defs, searchTerm = '') => {
  if (!answers) return [];
  const entries = flattenAnswers(answers);
  const pathologyGroupOrder = buildGroupOrderMap(answers?.patologie);
  const musclePathologyGroupOrder = buildGroupOrderMap(answers?.patologieMuscolo);
  const needle = (searchTerm || '').trim().toLowerCase();
  const defBuckets = defs.map((def) => ({ def, entries: [] }));
  const fallback = [];

  entries.forEach((entry) => {
    const haystack = `${entry.label} ${entry.display}`.toLowerCase();
    if (needle && !haystack.includes(needle)) return;

    const bucketIndex = defBuckets.findIndex(({ def }) => matchesSection(def, entry));
    if (bucketIndex >= 0) {
      defBuckets[bucketIndex].entries.push(entry);
    } else {
      fallback.push(entry);
    }
  });

  const sections = [];

  defBuckets.forEach(({ def, entries }) => {
    if (def.visibleIf && !def.visibleIf(answers)) return;
    if (!entries.length) return;

    entries.sort((a, b) => {
      const rootA = a.path[0];
      const rootB = b.path[0];
      const orderA = def.orderMap[rootA] ?? def.orderMap[a.key] ?? Number.MAX_SAFE_INTEGER;
      const orderB = def.orderMap[rootB] ?? def.orderMap[b.key] ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      if (rootA === 'patologie' && rootB === 'patologie') {
        const cmp = comparePathologyEntries(a, b, pathologyGroupOrder, PATHOLOGY_FIELD_ORDER_MAP);
        if (cmp !== 0) return cmp;
      }
      if (rootA === 'patologieMuscolo' && rootB === 'patologieMuscolo') {
        const cmp = comparePathologyEntries(a, b, musclePathologyGroupOrder, MUSCLE_PATHOLOGY_FIELD_ORDER_MAP);
        if (cmp !== 0) return cmp;
      }
      return a.label.localeCompare(b.label);
    });

    sections.push({
      key: def.key,
      title: def.title,
      entries,
    });
  });

  if (fallback.length) {
    fallback.sort((a, b) => a.label.localeCompare(b.label));
    sections.push({
      key: 'other',
      title: 'Altri campi',
      entries: fallback,
    });
  }

  return sections;
};

const sanitizeQuestionarioAnswers = (answers) => {
  if (!answers || typeof answers !== 'object') return answers;
  const sanitizedPatologie = filterPathologyGroup(answers.patologie);
  const sanitizedMuscolo = filterPathologyGroup(answers.patologieMuscolo);
  if (sanitizedPatologie === answers.patologie && sanitizedMuscolo === answers.patologieMuscolo) {
    return answers;
  }
  const clone = { ...answers };
  if (sanitizedPatologie) clone.patologie = sanitizedPatologie;
  else delete clone.patologie;
  if (sanitizedMuscolo) clone.patologieMuscolo = sanitizedMuscolo;
  else delete clone.patologieMuscolo;
  return clone;
};

const buildQuestionarioSections = (answers, searchTerm = '') =>
  buildSectionsFromDefs(sanitizeQuestionarioAnswers(answers), QUESTIONARIO_SECTION_DEFS, searchTerm);

const buildCheckFinaleSections = (answers, searchTerm = '') =>
  buildSectionsFromDefs(answers, CHECK_SECTION_DEFS, searchTerm);

export {
  prettifyKey,
  flattenAnswers,
  groupEntries,
  formatPrimitive,
  buildQuestionarioSections,
  buildCheckFinaleSections,
  isAffirmative,
  sanitizeQuestionarioAnswers,
};
